import Video from 'twilio-video';

class VideoService {
  constructor() {
    this.room = null;
    this.localTracks = [];
    this.participants = new Map();
    this.eventListeners = new Map();
  }

  async initialize() {
    try {
      // Get local media tracks
      this.localTracks = await Video.createLocalTracks({
        audio: true,
        video: { width: 640, height: 480, frameRate: 24 }
      });
      
      console.log('Local tracks initialized:', this.localTracks.length);
      return this.localTracks;
    } catch (error) {
      console.error('Failed to initialize local tracks:', error);
      throw error;
    }
  }

  async connectToRoom(roomName, identity) {
    try {
      // Get access token from backend
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/video/token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.getAuthToken()}`
        },
        body: JSON.stringify({
          user_id: identity,
          room_name: roomName,
          session_duration: 300
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to get token: ${response.statusText}`);
      }

      const { token } = await response.json();

      // Connect to Twilio Video room
      this.room = await Video.connect(token, {
        name: roomName,
        tracks: this.localTracks,
        dominantSpeaker: true,
        networkQuality: { local: 1, remote: 1 },
        bandwidthProfile: {
          video: {
            mode: 'collaboration',
            maxTracks: 2,
            dominantSpeakerPriority: 'high'
          }
        }
      });

      this.setupRoomEventListeners();
      return this.room;
    } catch (error) {
      console.error('Failed to connect to room:', error);
      throw error;
    }
  }

  setupRoomEventListeners() {
    if (!this.room) return;

    // Handle participant connections
    this.room.on('participantConnected', (participant) => {
      console.log('Participant connected:', participant.identity);
      this.participants.set(participant.sid, participant);
      this.setupParticipantEventListeners(participant);
      this.emit('participantConnected', participant);
    });

    // Handle participant disconnections
    this.room.on('participantDisconnected', (participant) => {
      console.log('Participant disconnected:', participant.identity);
      this.participants.delete(participant.sid);
      this.emit('participantDisconnected', participant);
    });

    // Handle dominant speaker changes
    this.room.on('dominantSpeakerChanged', (participant) => {
      this.emit('dominantSpeakerChanged', participant);
    });

    // Handle room disconnection
    this.room.on('disconnected', (room, error) => {
      console.log('Disconnected from room:', error ? error.message : 'Normal disconnect');
      this.cleanup();
      this.emit('disconnected', { room, error });
    });

    // Setup existing participants
    this.room.participants.forEach((participant) => {
      this.participants.set(participant.sid, participant);
      this.setupParticipantEventListeners(participant);
    });
  }

  setupParticipantEventListeners(participant) {
    // Handle track subscriptions
    participant.on('trackSubscribed', (track) => {
      console.log('Track subscribed:', track.kind, participant.identity);
      this.emit('trackSubscribed', { track, participant });
    });

    // Handle track unsubscriptions
    participant.on('trackUnsubscribed', (track) => {
      console.log('Track unsubscribed:', track.kind, participant.identity);
      this.emit('trackUnsubscribed', { track, participant });
    });

    // Handle existing tracks
    participant.tracks.forEach((publication) => {
      if (publication.isSubscribed) {
        this.emit('trackSubscribed', { 
          track: publication.track, 
          participant 
        });
      }
    });
  }

  getAuthToken() {
    // Integration with existing authentication system
    return localStorage.getItem('token') || sessionStorage.getItem('token');
  }

  // Event system for component integration
  on(event, callback) {
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, []);
    }
    this.eventListeners.get(event).push(callback);
  }

  off(event, callback) {
    if (this.eventListeners.has(event)) {
      const listeners = this.eventListeners.get(event);
      const index = listeners.indexOf(callback);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    }
  }

  emit(event, data) {
    if (this.eventListeners.has(event)) {
      this.eventListeners.get(event).forEach(callback => callback(data));
    }
  }

  // Media control methods
  async muteAudio() {
    const audioTrack = this.localTracks.find(track => track.kind === 'audio');
    if (audioTrack) {
      audioTrack.disable();
      return true;
    }
    return false;
  }

  async unmuteAudio() {
    const audioTrack = this.localTracks.find(track => track.kind === 'audio');
    if (audioTrack) {
      audioTrack.enable();
      return true;
    }
    return false;
  }

  async muteVideo() {
    const videoTrack = this.localTracks.find(track => track.kind === 'video');
    if (videoTrack) {
      videoTrack.disable();
      return true;
    }
    return false;
  }

  async unmuteVideo() {
    const videoTrack = this.localTracks.find(track => track.kind === 'video');
    if (videoTrack) {
      videoTrack.enable();
      return true;
    }
    return false;
  }

  disconnect() {
    console.log('🔌 VideoService.disconnect() called');
    if (this.room) {
      console.log('🔌 Disconnecting from Twilio room');
      this.room.disconnect();
    }
    console.log('🧹 Calling cleanup()');
    this.cleanup();
    console.log('✅ VideoService.disconnect() complete');
  }

  cleanup() {
    console.log('🧹 VideoService.cleanup() called');
    console.log('🛑 Stopping', this.localTracks.length, 'local tracks');
    this.localTracks.forEach((track, index) => {
      console.log(`🛑 Stopping track ${index}:`, track.kind, track.name);
      track.stop();
    });
    this.localTracks = [];
    this.participants.clear();
    this.room = null;
    console.log('✅ VideoService.cleanup() complete - all tracks stopped');
  }
}

export default new VideoService();